import React, { useState } from 'react';
import { ConfirmModal } from '../../ConfirmModal';
import { useAuthToken } from '../../../hooks/useAuthToken';

/**
 * CustomHeader - Componente header altamente configurabile
 *
 * Features:
 * - Layout Flex o Grid (2/3/4 colonne)
 * - Background: solido o gradiente con opacity
 * - Bordi configurabili (colore, spessore, radius)
 * - Logo: drag&drop, dimensioni, posizione, allineamento
 * - Titolo/Sottotitolo: font, colore, size, align
 * - Contenuti extra: 4 slot fissi + 4 dinamici
 * - Badge release
 * - Responsive: stack mobile automatico
 */

// ========== TYPES ==========

export type LayoutType = 'flex-3' | 'grid-2' | 'grid-3' | 'grid-4';
export type AlignType = 'left' | 'center' | 'right';
export type GradientDirection = 'to-r' | 'to-l' | 'to-t' | 'to-b' | 'to-tr' | 'to-tl' | 'to-br' | 'to-bl' | string;

export interface BackgroundConfig {
  type: 'solid' | 'gradient';
  solid?: {
    color: string;
    opacity: number; // 0-100
  };
  gradient?: {
    from: string;
    via?: string;
    to: string;
    direction: GradientDirection;
    opacity: number; // 0-100
  };
}

export interface BorderConfig {
  enabled: boolean;
  color: string;
  width: number; // px
  radius: number; // px
}

export interface LogoConfig {
  src: string;
  height: number; // px
  column?: number; // Per grid layout
  align: AlignType;
}

export interface TextConfig {
  text: string;
  font: 'inter' | 'montserrat' | 'sans' | 'serif' | 'mono';
  size: string; // Tailwind class: text-xl, text-2xl, etc.
  color: string; // hex
  align: AlignType;
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';
}

export interface ReleaseConfig {
  text: string;
  style: 'badge' | 'text';
  color?: string;
  bgColor?: string;
  onClick?: () => void; // Callback quando si clicca il badge
  modalTitle?: string; // Titolo modale info
  modalContent?: string | React.ReactNode; // Contenuto modale info
}

export interface LogoutConfig {
  enabled: boolean;
  isLoggedIn?: boolean; // true = verde, false = rosso (deprecato se useRealAuth è true)
  onLogoutClick?: () => void; // Callback quando si clicca logout
  loginUrl?: string; // URL per redirect se token scaduto
  labels?: {
    loggedIn?: string;
    loggedOut?: string;
  };
  useRealAuth?: boolean; // Se true, usa autenticazione JWT reale con countdown
  showCountdown?: boolean; // Se true, mostra countdown scadenza token
}

export interface ExtraContent {
  id: string;
  content: string | React.ReactNode;
  column?: number; // Per grid layout
  align?: AlignType;
  className?: string;
}

export interface ResponsiveConfig {
  mobile: 'stack' | 'hide-extras' | 'compact';
  tablet: 'compact' | 'full';
}

export interface CustomHeaderProps {
  // Dimensioni
  width?: 'full' | 'container' | string;
  height?: 'auto' | string;

  // Layout
  layout?: LayoutType;
  responsive?: ResponsiveConfig;

  // Background
  background?: BackgroundConfig;

  // Bordi
  border?: BorderConfig;

  // Logo
  logo?: LogoConfig;

  // Titoli
  title?: TextConfig;
  subtitle?: TextConfig;

  // Release
  release?: ReleaseConfig;

  // Logout
  logout?: LogoutConfig;

  // Contenuti extra (4 fissi + 4 dinamici)
  extraFixed?: ExtraContent[]; // Max 4
  extraDynamic?: ExtraContent[]; // Max 4

  // Custom className
  className?: string;
}

// ========== UTILS ==========

const getFontClass = (font: string): string => {
  const fontMap: Record<string, string> = {
    inter: 'font-sans',
    montserrat: 'font-montserrat',
    sans: 'font-sans',
    serif: 'font-serif',
    mono: 'font-mono',
  };
  return fontMap[font] || 'font-sans';
};

const getAlignClass = (align: AlignType): string => {
  const alignMap: Record<AlignType, string> = {
    left: 'text-left justify-start items-start',
    center: 'text-center justify-center items-center',
    right: 'text-right justify-end items-end',
  };
  return alignMap[align];
};

const getLayoutClasses = (layout: LayoutType, isMobile: boolean): string => {
  if (isMobile) return 'flex flex-col gap-4';

  const layoutMap: Record<LayoutType, string> = {
    'flex-3': 'flex flex-row justify-between items-center gap-6',
    'grid-2': 'grid grid-cols-2 gap-6 items-center',
    'grid-3': 'grid grid-cols-3 gap-6 items-center',
    'grid-4': 'grid grid-cols-4 gap-6 items-center',
  };
  return layoutMap[layout];
};

const getBackgroundStyle = (bg?: BackgroundConfig): React.CSSProperties => {
  if (!bg) return {};

  if (bg.type === 'solid' && bg.solid) {
    const opacity = bg.solid.opacity / 100;
    return {
      backgroundColor: bg.solid.color,
      opacity: opacity,
    };
  }

  if (bg.type === 'gradient' && bg.gradient) {
    const { from, via, to, direction, opacity } = bg.gradient;
    const opacityValue = opacity / 100;

    // Converti direzione da 'to-r' a 'to right', oppure usa valore custom (es. '30deg')
    const directionMap: Record<string, string> = {
      'to-r': 'to right',
      'to-l': 'to left',
      'to-t': 'to top',
      'to-b': 'to bottom',
      'to-tr': 'to top right',
      'to-tl': 'to top left',
      'to-br': 'to bottom right',
      'to-bl': 'to bottom left',
    };
    const cssDirection = directionMap[direction] || direction; // Se non trovato, usa valore custom

    let gradientString = `linear-gradient(${cssDirection}, ${from}`;
    if (via) gradientString += `, ${via}`;
    gradientString += `, ${to})`;

    return {
      backgroundImage: gradientString,
      opacity: opacityValue,
    };
  }

  return {};
};

// ========== COMPONENT ==========

const CustomHeader: React.FC<CustomHeaderProps> = ({
  width = 'full',
  height = 'auto',
  layout = 'flex-3',
  responsive = { mobile: 'stack', tablet: 'full' },
  background,
  border = { enabled: false, color: '#e2e8f0', width: 1, radius: 0 },
  logo,
  title,
  subtitle,
  release,
  logout,
  extraFixed = [],
  extraDynamic = [],
  className = '',
}) => {
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showReleaseModal, setShowReleaseModal] = useState(false);

  // Usa autenticazione reale se richiesto
  const authToken = logout?.useRealAuth ? useAuthToken() : null;

  // Determina stato autenticazione (reale o mock)
  const isLoggedIn = logout?.useRealAuth
    ? authToken?.isAuthenticated ?? false
    : logout?.isLoggedIn ?? false;

  const handleLogoutClick = () => {
    if (isLoggedIn) {
      // Se loggato → mostra modale conferma
      setShowLogoutModal(true);
    } else {
      // Se token scaduto → redirect login
      if (logout?.loginUrl) {
        window.location.href = logout.loginUrl;
      } else {
        logout?.onLogoutClick?.();
      }
    }
  };

  const handleLogoutConfirm = () => {
    console.log('🔵🔵🔵 [CustomHeader] handleLogoutConfirm chiamato!');
    console.log('🔵 [CustomHeader] logout config:', logout);
    console.log('🔵 [CustomHeader] onLogoutClick:', logout?.onLogoutClick);
    setShowLogoutModal(false);

    if (logout?.onLogoutClick) {
      console.log('🔵 [CustomHeader] Chiamando onLogoutClick...');
      logout.onLogoutClick();
      console.log('🔵 [CustomHeader] onLogoutClick chiamato!');
    } else {
      // FALLBACK: Se onLogoutClick non è definito, fai logout diretto
      console.warn('🔵 [CustomHeader] ⚠️ onLogoutClick NON DEFINITO - Eseguo logout fallback!');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      console.log('🔵 [CustomHeader] Token rimossi, redirect a login...');
      window.location.href = logout?.loginUrl || '/login';
    }
  };

  // Width classes
  const widthClass = width === 'full'
    ? 'w-full'
    : width === 'container'
    ? 'max-w-7xl mx-auto'
    : '';

  // Height classes e style
  const heightClass = height === 'auto' ? 'h-auto' : '';
  const heightStyle = height !== 'auto' ? { height } : {};

  // Border styles
  const borderStyle: React.CSSProperties = border.enabled ? {
    borderWidth: `${border.width}px`,
    borderColor: border.color,
    borderRadius: `${border.radius}px`,
    borderStyle: 'solid',
  } : {};

  // Background style per layer separato
  const bgStyle = getBackgroundStyle(background);

  // Layout classes (responsive aware)
  const layoutClasses = `
    ${getLayoutClasses(layout, false)}
    md:${getLayoutClasses(layout, false)}
    ${responsive.mobile === 'stack' ? 'flex-col sm:flex-row' : ''}
  `;

  return (
    <header
      className={`
        relative overflow-hidden
        ${widthClass}
        ${heightClass}
        ${layoutClasses}
        px-4 sm:px-6 lg:px-8
        py-3 sm:py-4 lg:py-6
        ${className}
      `}
      style={{ ...borderStyle, ...heightStyle }}
    >
      {/* Background Layer con opacity */}
      {background && (
        <div
          className="absolute inset-0"
          style={bgStyle}
        />
      )}
      {/* Logo */}
      {logo && (
        <div className={`relative z-10 flex ${getAlignClass(logo.align)}`}>
          <img
            src={logo.src}
            alt="Logo"
            style={{ height: `${logo.height}px` }}
            className="object-contain"
          />
        </div>
      )}

      {/* Titoli */}
      {(title || subtitle) && (
        <div className={`relative z-10 flex flex-col gap-1 ${title?.align ? getAlignClass(title.align) : ''}`}>
          {title && (
            <h1
              className={`
                ${getFontClass(title.font)}
                ${title.size}
                ${title.weight ? `font-${title.weight}` : 'font-bold'}
              `}
              style={{ color: title.color }}
            >
              {title.text}
            </h1>
          )}

          {subtitle && (
            <p
              className={`
                ${getFontClass(subtitle.font)}
                ${subtitle.size}
                ${subtitle.weight ? `font-${subtitle.weight}` : 'font-normal'}
              `}
              style={{ color: subtitle.color }}
            >
              {subtitle.text}
            </p>
          )}
        </div>
      )}

      {/* User Info + Release Badge + Logout (raggruppati insieme) */}
      {(extraFixed.length > 0 || release || logout?.enabled) && (
        <div className="relative z-10 flex gap-1.5 items-center">
          {/* Extra Fixed Content (user info, etc.) */}
          {extraFixed.slice(0, 8).map((extra) => (
            <div key={extra.id}>
              {typeof extra.content === 'string' ? (
                <span>{extra.content}</span>
              ) : (
                extra.content
              )}
            </div>
          ))}

          {/* Release Badge */}
          {release && (
            <button
              onClick={() => {
                release.onClick?.();
                if (release.modalTitle || release.modalContent) {
                  setShowReleaseModal(true);
                }
              }}
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 bg-blue-500/20 text-blue-400 border border-blue-500/40 hover:bg-blue-500/30 cursor-pointer"
              title="Informazioni versione"
            >
              {release.text}
            </button>
          )}

          {/* Logout Button with Optional Countdown */}
          {logout?.enabled && (
            <button
              onClick={handleLogoutClick}
              className={`
                flex flex-col items-center justify-center gap-1 px-4 rounded-lg text-sm font-semibold
                transition-all duration-300 cursor-pointer backdrop-blur-sm h-[48px]
                ${isLoggedIn
                  ? 'bg-gradient-to-br from-emerald-600/90 to-green-700/90 text-white border border-emerald-400/40 hover:from-emerald-500/95 hover:to-green-600/95 hover:border-emerald-300/50 hover:scale-[1.02] hover:shadow-xl hover:shadow-emerald-500/30'
                  : 'bg-gradient-to-br from-rose-600/90 to-red-700/90 text-white border border-rose-400/40 hover:from-rose-500/95 hover:to-red-600/95 hover:border-rose-300/50 hover:scale-[1.02] hover:shadow-xl hover:shadow-rose-500/30'
                }
              `}
              title={isLoggedIn ? 'Logout' : 'Token scaduto - Vai al login'}
            >
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full animate-pulse ${isLoggedIn ? 'bg-emerald-200' : 'bg-rose-200'}`}
                />
                <span className="drop-shadow-md">
                  {isLoggedIn
                    ? (logout.labels?.loggedIn || 'Logout')
                    : (logout.labels?.loggedOut || 'Login')
                  }
                </span>
              </div>

              {/* Token Countdown - Inside Button */}
              {logout.showCountdown && logout.useRealAuth && authToken && authToken.isAuthenticated && (
                <div className={`text-[11px] font-medium drop-shadow ${
                  authToken.timeRemaining < 300 // < 5 minuti
                    ? 'text-amber-200'
                    : 'text-emerald-50'
                }`}>
                  ⏱ {authToken.timeRemainingFormatted}
                </div>
              )}
            </button>
          )}
        </div>
      )}

      {/* Contenuti Extra Dinamici */}
      {extraDynamic.length > 0 && (
        <div className="relative z-10 flex gap-4 items-center flex-wrap">
          {extraDynamic.slice(0, 4).map((extra) => (
            <div
              key={extra.id}
              className={`${extra.align ? getAlignClass(extra.align) : ''} ${extra.className || ''}`}
            >
              {typeof extra.content === 'string' ? (
                <span>{extra.content}</span>
              ) : (
                extra.content
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modale Conferma Logout */}
      {logout?.enabled && (
        <ConfirmModal
          isOpen={showLogoutModal}
          onClose={() => setShowLogoutModal(false)}
          onConfirm={handleLogoutConfirm}
          title="Conferma Logout"
          message="Sei sicuro di voler uscire dall'applicazione?"
          variant="warning"
        />
      )}

      {/* Modale Info Release */}
      {release && (release.modalTitle || release.modalContent) && (
        <ConfirmModal
          isOpen={showReleaseModal}
          onClose={() => setShowReleaseModal(false)}
          onConfirm={() => setShowReleaseModal(false)}
          title={release.modalTitle || `Informazioni ${release.text}`}
          message={typeof release.modalContent === 'string' ? release.modalContent : 'Informazioni sulla release'}
          variant="info"
        />
      )}
    </header>
  );
};

export default CustomHeader;
